import { useRequiredContext } from "@redotech/react-util/context";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { NewReturnItemData } from "@redotech/redo-customer-portal-shared/flow/util";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import { PendingReturnItem } from "@redotech/redo-model/draft-return/draft-return-items";
import {
  OfferingSelection,
  offeringSelectionSchema,
} from "@redotech/redo-model/extended-warranty";
import { FieldId, Step } from "@redotech/redo-model/return-flow";
import { SubmitWarrantyRegistrationInput } from "@redotech/redo-model/warranties/submit-warranty-registration-schema";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { useNavigate } from "react-router-dom";

type SubmitWarrantyRegistrationResponse = {
  submitAndNavigateToWarrantyRegistration: (
    input: SubmitWarrantyRegistrationInput,
  ) => Promise<void>;
  submitWarrantyRegistration: (
    input: SubmitWarrantyRegistrationInput,
  ) => Promise<void>;
};

// function generateWarrantyRegistrationUrl(
//   warrantyRegistrationId: string,
//   customer: User,
//   customerToken: string,
// ) {
//   const next = `/returns-portal/warranties/${warrantyRegistrationId}/details`;
//   const baseUrl = "/returns-portal/login";
//   return `${baseUrl}?next=${encodeURIComponent(next)}&token=${customerToken}&customer=${JSON.stringify(customer)}`;
// }
function generateWarrantyRegistrationUrl(warrantyRegistrationId: string) {
  return `/returns-portal/warranties/${warrantyRegistrationId}/details`;
}
export interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  address: {
    street1: string;
    street2: string;
    state: string;
    city: string;
    zip: string;
    country: string;
  };
}

export interface Response {
  type: "images" | "text_area" | "videos" | "multiple_choice";
  value: string | string[] | URL[];
  message: string;
}
// TODO: Seems weird that I would need to create a new type for this
type CleanedResponse = Omit<Response, "value"> & { value: string | string[] };

export function getWarrantyRegistrationData(
  item: NewReturnItemData | PendingReturnItem,
) {
  const responses: Response[] = [];
  const userInfo: UserInfo = {
    firstName: "",
    lastName: "",
    email: "",
    address: {
      street1: "",
      street2: "",
      state: "",
      city: "",
      zip: "",
      country: "",
    },
  };
  const ewUpsellInputAnswer = item.inputAnswers?.find(
    (answer) => answer.fieldId === "warranties/path/ew-upsell",
  );
  let offeringSelection: OfferingSelection | undefined;
  if (ewUpsellInputAnswer && typeof ewUpsellInputAnswer.value === "string") {
    const ewUpsellInputAnswerValue = JSON.parse(ewUpsellInputAnswer.value);
    const parsedOffering = offeringSelectionSchema.safeParse(
      ewUpsellInputAnswerValue,
    );
    offeringSelection = parsedOffering.success
      ? parsedOffering.data
      : undefined;
  }
  let optInMarketing = false;
  // Process input answers
  item.inputAnswers?.forEach((answer) => {
    const value = Array.isArray(answer.value) ? answer.value[0] : answer.value;
    if (!answer.fieldId) {
      if (["images", "text_area", "videos"].includes(answer.type)) {
        responses.push({
          type: answer.type as Response["type"],
          value: answer.value,
          message: answer.message,
        });
      }
    } else {
      const [category, subcategory, field] = answer.fieldId.split("/");
      if (category === "accounts") {
        if (subcategory === "user") {
          if (field === "marketing") {
            optInMarketing = value === "true";
          } else {
            (userInfo as any)[field] = value;
          }
        } else if (subcategory === "address") {
          userInfo.address[field as keyof UserInfo["address"]] =
            value.toString();
        }
      }
    }
  });

  // Process multiple choice answers
  item.multipleChoiceAnswers.forEach(({ step, answer }) => {
    const stepObject = item.flow.steps[step] as Step.MultipleChoice;
    responses.push({
      type: "multiple_choice",
      message: stepObject.message,
      value: stepObject.options[answer].name,
    });
  });

  const isRegisterOnly = !!item.multipleChoiceAnswers.find((response) => {
    if ("fieldId" in response) {
      return response.fieldId === FieldId.WARRANTIES_PATH_SUBMIT_REGISTRATION;
    }
    return false;
  });

  return {
    responses,
    userInfo,
    offeringSelection,
    optInMarketing,
    isRegisterOnly,
  };
}

export function cleanResponses(responses: Response[]): CleanedResponse[] {
  return responses.map((response) => ({
    ...response,
    value: Array.isArray(response.value)
      ? response.value.map((url) => url.toString())
      : response.value,
  }));
}

export function useSubmitWarrantyRegistration({
  setDraftReturn,
}: {
  setDraftReturn?: (draftReturn: DraftReturn) => void;
}): SubmitWarrantyRegistrationResponse {
  const client = useRequiredContext(RpcClientContext);
  const navigate = useNavigate();

  const submitWarrantyRegistration = async (
    input: SubmitWarrantyRegistrationInput,
  ) => {
    const {
      // Disabling login because of security concerns. Will be re-enabled when we have a OTP in registration.
      // customer: customerResponse,
      // customerToken,
      draftReturn: updatedDraftReturn,
      warrantyRegistrationId,
    } = await client.submitWarrantyRegistration(input);

    if (
      !updatedDraftReturn ||
      // !customerResponse ||
      // !customerToken ||
      !warrantyRegistrationId
    ) {
      throw new Error("Failed to submit warranty registration");
    }

    // const customer = {
    //   ...customerResponse,
    //   customerId: customerResponse.customerId ?? undefined,
    // };

    if (setDraftReturn) {
      setDraftReturn(updatedDraftReturn);
    }

    // setAuth(customerToken);
    // setUser(customer);

    // return { warrantyRegistrationId, customer, customerToken };
    return { warrantyRegistrationId };
  };

  return {
    submitAndNavigateToWarrantyRegistration: async (
      input: SubmitWarrantyRegistrationInput,
    ) =>
      alertOnFailure("Could not submit and navigate to warranty registration")(
        async () => {
          const { warrantyRegistrationId } =
            await submitWarrantyRegistration(input);

          const url = generateWarrantyRegistrationUrl(
            warrantyRegistrationId,
            // customer,
            // customerToken,
          );
          navigate(url);
          return;
        },
      ),
    submitWarrantyRegistration: async (
      input: SubmitWarrantyRegistrationInput,
    ) =>
      alertOnFailure("Could not submit warranty registration")(async () => {
        await submitWarrantyRegistration(input);
      }),
  };
}
